import { createContext, useContext } from 'react';
import type { Lang } from '@/i18n';
import type { User } from './authClient';

/**
 * The auth context lives in its OWN module, apart from `useAuth.tsx`, for one
 * concrete reason: 20+ tests `vi.mock('@/features/auth/useAuth', …)` to inject a
 * fake `useAuth`, and that mock replaces the whole module. `useTenantWritable`
 * must read the REAL context regardless of that mock (it gates write affordances
 * app-wide, slice 11b), so it imports the context from HERE — a module those
 * mocks do not touch — and reads `null` (→ writable) when no provider is mounted,
 * exactly as it does in production for a non-tenant session.
 */

export type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; user: User }
  // A STAFF principal signed in on the tenant origin. There is no fleet to show
  // them — RequireAuth renders the in-place panel (17i). A distinct branch, not
  // a flavour of `authenticated`, so no protected screen ever tries to read a
  // tenant `user` that does not exist.
  | { status: 'staff' }
  | { status: 'unauthenticated' };

export interface AuthContextValue {
  state: AuthState;
  login: (phone: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /**
   * Patch the signed-in user's own profile and adopt the result (slice 16).
   *
   * 🔴 THIS IS THE ONLY WAY THE LANGUAGE PREFERENCE IS CHANGED. Both surfaces
   * that offer it — the top-bar `LangToggle` and the Settings screen — call
   * THIS, so there is one preference with two controls rather than two
   * preferences that disagree. Writing localStorage directly is what caused the
   * defect this slice fixes.
   */
  updateProfile: (patch: { name?: string; language?: Lang }) => Promise<void>;
  /**
   * Re-read `/api/me` and adopt it. Used after a change that alters the profile
   * WITHOUT going through `updateProfile` — setting or removing the command PIN
   * flips `hasCommandPin`, and the Raad Secure control reads that off the auth
   * user, so a stale value would leave the control asking for a PIN the user has
   * already set (or offering one they have just removed). A no-op for a
   * non-tenant principal.
   */
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * The auth context, or `null` when no `<AuthProvider>` is mounted. Never throws —
 * for a hook that must render harmlessly outside the provider (`useTenantWritable`
 * is mounted in isolation-tested surfaces that never wrap the provider). Prefer
 * `useAuth` anywhere a provider is guaranteed.
 */
export function useOptionalAuth(): AuthContextValue | null {
  return useContext(AuthContext);
}
